import * as React from 'react';

import { useQueryClient, MutationStatus } from '@tanstack/react-query';
import queryKeys from '../api/queryKeys';

import useGetFlightEventReport from '../api/hooks/flight-event/useGetFlightEventReport';
import useUpdateFlightEvent from '../api/hooks/flight-event/useUpdateFlightEvent';
import useSubmitFlightEvent from '../api/hooks/flight-event/useSubmitFlightEvent';
import useApproveFlightEvent from '../api/hooks/flight-event/useApproveFlightEvent';
import useGetAllowedActions from '../api/hooks/common/useGetAllowedActions';

import {
  AllowedActions,
  AuthorizationRequestType,
  EventReportApprovalRequest,
  FlightEventReport,
  FlightEventReportDTO,
  ReportType,
} from '../api/clients';

import LoadingOverlay from '../../components/LoadingOverlay';
import ErrorOverlay from '../../components/ErrorOverlay';
import useTitle from '../api/hooks/common/useTitle';
import useSendNotification from '../api/hooks/flight-event/useSendNotification';

type IFlightEventReportData = {
  flightEventReport: FlightEventReport;
  allowedActions: AllowedActions | undefined;

  update: (
    data: FlightEventReport,
    successCallback?: any,
    failCallback?: any
  ) => void;
  updateStatus: MutationStatus;

  submit: (
    eventId: number | undefined,
    successCallback?: any,
    failCallback?: any,
  ) => void;
  submitStatus: MutationStatus;

  approve: (
    request: EventReportApprovalRequest,
    successCallback?: any,
    failCallback?: any,
  ) => void;
  approveStatus: MutationStatus;

  sendNotification: (
    request: EventReportApprovalRequest,
    successCallback?: any,
    failCallback?: any,
  ) => void;
  sendNotificationStatus: MutationStatus;
};

const FlightEventReportDataContext = React.createContext<IFlightEventReportData>(undefined!);

export function FlightEventReportDataProvider({
  children,
  flightEventReportId,
  role,
}: {
  children: React.ReactNode,
  flightEventReportId: number,
  role?: AuthorizationRequestType,
}) {
  const queryClient = useQueryClient();
  useTitle(document.documentURI);
  const {
    data: reportData,
    status: reportStatus,
    error: reportError,
  } = useGetFlightEventReport(flightEventReportId, role || AuthorizationRequestType.Contribute);

  const { data: allowedActions } = useGetAllowedActions(
    reportData?.employeeId,
    reportData?.status,
    ReportType.FlightEventReport,
  );

  const { status: updateMutationStatus, mutate: updateMutation } = useUpdateFlightEvent();
  const { status: approveMutationStatus, mutate: approveMutation } = useApproveFlightEvent();
  const { status: resendNotificationMutationStatus, mutate: resendNotificationMutation } = useSendNotification();
  const { status: submitMutationStatus, mutate: submitMutation } = useSubmitFlightEvent();

  const onSuccessfulMutation = (
    returnData: FlightEventReportDTO,
    eventId: number | undefined,
    successCallback?: any,
    failCallback?: any,
  ) => {
    const { eventReport, error } = returnData;

    queryClient.setQueryData([queryKeys.FlightEvent.FlightEventReport, { id: eventId }], eventReport);
    if (!error) {
      if (successCallback) successCallback(eventReport);
    } else if (failCallback) failCallback(error);
  };

  const flightEventReportData = React.useMemo<IFlightEventReportData>(() => ({
    flightEventReport: reportData!,
    allowedActions: allowedActions!,

    updateStatus: updateMutationStatus,
    update: (data: FlightEventReport, successCallback?: any, failCallback?: any) => {
      updateMutation(data, {
        onSuccess: (returnData) => onSuccessfulMutation(
          returnData,
          data.id,
          successCallback,
          failCallback,
        ),
      });
    },

    approveStatus: approveMutationStatus,
    approve: (request: EventReportApprovalRequest, successCallback?: any, failCallback?: any) => {
      if (request.eventReportId !== undefined) {
        approveMutation(request, {
          onSuccess: (returnData) => onSuccessfulMutation(
            returnData,
            request.eventReportId,
            successCallback,
            failCallback,
          ),
        });
      }
    },
    sendNotificationStatus: resendNotificationMutationStatus,
    sendNotification: (request: EventReportApprovalRequest, successCallback?: any, failCallback?: any) => {
      if (request.eventReportId !== undefined) {
        resendNotificationMutation(request, {
          onSuccess: (returnData) => onSuccessfulMutation(
            returnData,
            request.eventReportId,
            successCallback,
            failCallback,
          ),
        });
      }
    },
    submitStatus: submitMutationStatus,
    submit: (eventId: number | undefined, successCallback?: any, failCallback?: any) => {
      if (eventId !== undefined) {
        submitMutation(eventId, {
          onSuccess: (returnData) => onSuccessfulMutation(
            returnData,
            eventId,
            successCallback,
            failCallback,
          ),
        });
      }
    },
  }), [
    reportData,
    allowedActions,
    updateMutationStatus,
    approveMutationStatus,
    submitMutationStatus,
  ]);

  if (reportStatus === 'pending') {
    return (
      <LoadingOverlay />
    );
  }

  if (reportStatus === 'error') {
    return (
      <ErrorOverlay
        title={reportError?.title}
        message={`An error occurred retrieving data for form: ${flightEventReportId}`}
      />
    );
  }

  if (reportStatus === 'success' && reportData !== undefined) {
    return (
      <FlightEventReportDataContext.Provider value={flightEventReportData}>
        {children}
      </FlightEventReportDataContext.Provider>
    );
  }

  return null;
}

export const useFlightEventReportData = () => React.useContext(FlightEventReportDataContext);
